import React, { createContext, useContext, useEffect, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { endsWith, find, get, isEmpty, isArray } from 'lodash';
import { useSnackbar } from 'notistack';

import {
  useGetInstrumentQuery,
  useGetInstrumentDraftQuery,
  useGetInstrumentKeysQuery,
  useSearchInstrumentNewQuery,
  useSaveInstrumentDraftMutation,
  useLazyPublishQuery,
  useLazyUnpublishQuery, useLazyRevertToPublishedQuery, useLazyDeleteDraftQuery
} from 'app/apis/instruments.api';
import { useGetListsQuery } from 'app/apis/lists.api';
import { extractAppendices, parseHtml, slugify } from 'utils/html-helper';

const InstrumentContext = createContext({});

export const InstrumentProvider = ({ children }) => {
  const { lang, instrumentKey, instrumentId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [saveInstrument, { isLoading: isSaveLoading }] = useSaveInstrumentDraftMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [subContent, setSubContent] = useState([]);

  const isPreview = searchParams.get('preview') === 'true';
  const isEdit = location.pathname.includes('/edit');
  const isCreate = endsWith(location.pathname, '/create');

  const shouldLoadPublished = !isEdit && !isPreview && !isCreate;
  const shouldLoadDraft = !isCreate && (isPreview || isEdit);
  const shouldLoadNew = isCreate;

  const { data: publishedInstrument, isFetching: isInstrumentFetching } = useGetInstrumentQuery(
    { lang, instrumentKey },
    { skip: !shouldLoadPublished }
  );

  const { data: instrumentDraft, isFetching: isInstrumentDraftFetching } = useGetInstrumentDraftQuery(
    { lang, instrumentId: isPreview ? instrumentKey : instrumentId },
    { skip: !shouldLoadDraft }
  );

  const { data: instrumentNew, isFetching: instrumentNewFetching } = useSearchInstrumentNewQuery(
    {},
    { skip: !shouldLoadNew }
  );

  const { data: instrumentListEn } = useGetListsQuery({ lang: 'en' });
  const { data: instrumentList } = useGetListsQuery({ lang });

  const { data: instrumentKeys } = useGetInstrumentKeysQuery({ lang });

  const [publishTrigger, { isLoading: isPublishLoading }] = useLazyPublishQuery();
  const [unpublishTrigger, { isLoading: isUnpublishLoading }] = useLazyUnpublishQuery();
  const [revertTrigger, { isLoading: isRevertToPublishLoading }] = useLazyRevertToPublishedQuery();
  const [deleteTrigger, { isLoading: isDeleteLoading }] = useLazyDeleteDraftQuery();

  let instrument = null;
  if(shouldLoadDraft) instrument = instrumentDraft;
  else if(shouldLoadNew) instrument = instrumentNew;
  else instrument = publishedInstrument;

  const isLoading = isInstrumentFetching || isInstrumentDraftFetching || instrumentNewFetching;

  const onSave = async (newInstrument, id) => {
    try {
      const saveInstrumentResult = await saveInstrument({ body: newInstrument, id: id }).unwrap();
      enqueueSnackbar('saved', { variant: 'success' });
      let tmp = location.pathname.split('/');
      if (tmp[tmp.length - 1] === 'create') {
        tmp.splice(-1);
        navigate(`${tmp.join('/')}/${id}/edit`)
      } else if (tmp[tmp.length - 2] === 'create') {
        tmp.splice(-2);
        navigate(`${tmp.join('/')}/${id}/edit`)
      }
    } catch (e) {
      const errorInfo = get(e, 'data.message', get(e, 'data.errorCode'));
      if(errorInfo) {
        enqueueSnackbar(errorInfo, { variant: 'error' })
      } else {
        enqueueSnackbar('error', { variant: 'error' })
      }
    }
  }

  const checkCanDownloadBooklet = (instrument) => {
    if (isEmpty(instrument)) return false;
    if (instrument.bookletDeactivated) return false;

    const manuallyUploadedBooklet = get(instrument, `manuallyUploadedBooklet.${lang}`);
    if (manuallyUploadedBooklet) return true;

    if(instrument.manuallyUploadedBooklet && isArray(instrument.manuallyUploadedBooklet)) {
      const langBooklet = find(instrument.manuallyUploadedBooklet, { lang });
      return !isEmpty(langBooklet);
    }

    const langBodyTextRef = find(get(instrument, 'bodyText.ref'), { lang });
    return !endsWith(get(langBodyTextRef, 'uri'), '.pdf');
  };

  const checkIfFieldExists = (field) => {
    switch (field) {
      case "backgroundInformation":
        return find(get(instrument, 'background.text'), r => r.lang === lang)?.value ||
          find(get(instrument, 'blurb.text'), r => r.lang === lang)?.value;
      case "supportDocuments":
        return get(instrument, 'bodyText.support');
      case "translations":
        return get(instrument, 'translations');
      case "committees":
        return get(instrument, 'committees.parentCommittee');
      case "involvedCommittees":
        return get(instrument, 'committees.involvedCommittee');
      case "dates":
        return get(instrument, 'changeHistory.update');
      case "relatedInstruments":
        return get(instrument, 'relations');
      case "monitoring":
        return find(get(instrument, 'monitoring.entry[0].comments.text'), r => r.lang === lang)?.value;
      case "leg":
        let note = get(instrument, 'notes');
        return note && note.value;
      default:
        return false;
    }
  }

  const purify = (obj) => {
    let tmp = JSON.parse(JSON.stringify(obj));
    if (tmp.translations?.translation === undefined || tmp.translations?.translation?.length === 0) {
      delete tmp.translations
    }

    if (tmp.themes?.theme === undefined) {
      delete tmp.themes
    }

    if (tmp.relations === undefined) {
      delete tmp.relations
    }
    if (tmp.notes === undefined) {
      delete tmp.notes
    }
    if (tmp.hostedDocuments === undefined || tmp.hostedDocuments?.length === 0) {
      delete tmp.hostedDocuments
    }
    if (tmp.coverImage === undefined) {
      delete tmp.coverImage
    }
    if (tmp.comments === undefined) {
      delete tmp.comments
    }
    if (tmp.changeHistory?.update === undefined) {
      delete tmp.changeHistory
    }

    if (tmp.committees?.involvedCommittee === undefined || tmp.committees?.involvedCommittee?.length === 0) {
      delete tmp.committees?.involvedCommittee
    }
    if (tmp.committees?.parentCommittee === undefined || tmp.committees?.parentCommittee?.length === 0) {
      delete tmp.committees?.parentCommittee
    }

    if (tmp.committees && Object.keys(tmp.committees).length === 0) {
      delete tmp.committees
    }
    if (tmp.manuallyUploadedBooklet === undefined || tmp.manuallyUploadedBooklet?.length === 0) {
      delete tmp.manuallyUploadedBooklet
    }

    if (tmp.tags === undefined || tmp.tags?.tag === undefined || tmp.tags?.tag === null || tmp.tags?.tag?.length === 0) {
      delete tmp.tags
    }

    delete tmp.statusSummary;

    return tmp;
  }

  const m = async (m, location) => {
    try {
      let res = await m({ id: instrument.id }).unwrap();
      if (location) {
        navigate(location)
      } else {
        window.location.reload();
      }
    } catch(e) {
      const errorInfo = get(e, 'data.message', get(e, 'data.errorCode'));
      if(errorInfo) {
        enqueueSnackbar(errorInfo, { variant: 'error' })
      } else {
        enqueueSnackbar('error', { variant: 'error' })
      }
    }
  }

  const onPublish = async () => {
    await m(publishTrigger, `/${lang}/instruments/${instrument?.id}?preview=false`);
  }

  const onUnpublish = () => {
    m(unpublishTrigger);
  }

  const onRevertToPublish = () => {
    m(revertTrigger);
  }

  const onDelete = () => {
    m(deleteTrigger);
  }

  const constructContent = (text) => {
    let txt = extractAppendices(text);
    if (txt.length === 2) {
      /*ex. <span style="font-size: 10pt;">(iii)<span style="font-size-adjust: none; font-stretch: normal;">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                          </span></span>
                         */
      // remove ms word's html converter's oddities
      let html = txt[1].replace(/font(?:-family)?:((("|'|&quot;)?[\w\s-]+)\3\s?,?\s?)+/ig, "")
        .replace(/font(?:-size)\s*:\s*[0-9]*(\.[0-9]+)?(pt|px|em|")?;?/ig, "")
        .replace(/<span[\s0-9a-zA-Z\-"';%\.:&=]*>(?:\s?&nbsp;\s?)+<\/span>/ig,
          '<span style="margin-right:9pt"></span>')
        .replace(/<span([\s0-9A-Za-z"':;%&=,\.\-]*)>((\(?[0-9a-zA-Z]{0,}(\.|•|-|·|●)?\)?){0,6}[0-9a-zA-Z]?(\.|\))?)(<[a-zA-Z]*>)?(\s|&nbsp;){2,}(<\/[a-zA-Z]*>)?/img,
          "<span style='margin-right:9pt;'>$2</span><span$1>")
        //ATTN! the dot characters below are NOT the same!
        .replace(/<span style='margin-right:9pt;'>\s*[•·]\s*</img, "<span>●<")
        //.replace( /<span[\s0-9a-zA-Z\-"';%\.:&=]*>\s*[·\.]\s*</img, "<span>●<" )
        .replace(/text-indent:\s*-[0-9]*\.?[0-9]+(pt|cm|mm|px|in|em|ex|pc)/img, "");
      // extract links from instrument main text

      const doc = parseHtml(html)

      const links = doc.getElementsByTagName('a');

      let sbc = [];

      for (const link of links) {
        let linkHref = link.getAttribute('href');
        if (linkHref) {
          if (linkHref === '#' && link.innerText) {
            var linkSlug = slugify(link.innerText);
            link.id = linkSlug;
            link.removeAttribute('href');
            link.classList.add('licomp-details-htmlText-anchor');
            sbc.push({ id: linkSlug, name: link.innerText });
          } else if (linkHref.endsWith('.pdf') && link.target !== '_blank') {
            link.target = '_blank';
          }
        }

        if (sbc.length > 0) {
          setSubContent(sbc)
        }
      }

      return doc.documentElement.outerHTML;
    }
  }

  const loadingStates = {
    isSaveLoading, isPublishLoading, isUnpublishLoading, isRevertToPublishLoading, isDeleteLoading, isLoading
  };
  const actions = {
    onSave, onDelete, purify, checkIfFieldExists, onPublish, onUnpublish, onRevertToPublish, constructContent, checkCanDownloadBooklet
  };
  const value = {
    instrument, instrumentList, instrumentListEn, instrumentKeys, isPreview, subContent,
    ...actions, ...loadingStates,
  };

  return (
    <InstrumentContext.Provider value={value}>
      {isLoading && <LinearProgress/>}
      {instrument && children}
    </InstrumentContext.Provider>
  );
};

export const useInstrument = () => useContext(InstrumentContext);
